import * as yup from 'yup';

import {
  CanvasSizeEnum,
  ComparisonLineValueTypeEnum,
  CreatedWithEnum,
  DataOriginEnum,
  DownloadImageFormatEnum,
  DownloadScalePngEnum,
  FontFamilyEnum,
  LabelPosEnum,
  NrOfDecimalsEnum,
  PublicPermissionEnum,
  ReportingPeriodEnum,
  SecondLabelLineValueTypeEnum,
  StatementTypeEnum,
  TeamPermissionEnum,
} from '../generated-api';
import { isNumeric } from './number';

const emptyCompanyNameError =
  'Company name cannot be empty but you can set your custom title below to hide it';
const emptyCustomTitleError =
  'Custom title cannot be empty but you can hide it via View > Show diagram title';

// Validates that a string contains at least one non-whitespace character
const containsNonWhitespace = /^(?!\s*$).+/;

/*
 * If use_custom_title is true, make sure it's not just whitespaces (or empty), otherwise it is nullable.
 * See https://stackoverflow.com/a/62332386
 */
const customTitleSchema = yup
  .string()
  .max(255)
  .when(['use_custom_title'], (use_custom_title, schema) =>
    use_custom_title[0]
      ? schema.matches(containsNonWhitespace, emptyCustomTitleError)
      : schema.nullable()
  );

/*
 * This schema is used as the single source of truth in React for what constitutes a valid Sankey model.
 * It is strongly typed meaning that the compiler will complain if some of the fields are missing or if
 * the individual fields' validation is weaker than the one in the generated-api types.
 * Helpers at the bottom of the file can be used for extracting constraints from the schema if they are
 * needed outside of the Sankey form, e.g. for setting the min/max values of a slider.
 */
const schema = {
  canvas_height: yup.number().integer().min(100).max(10000).nullable(),
  canvas_size: yup.mixed<CanvasSizeEnum>(),
  canvas_width: yup.number().integer().min(100).max(10000).nullable(),
  // TODO: Converge between same validation on frontend and backend
  chart: yup.object({
    type: yup.mixed<'sankey'>().required(),
    version: yup.mixed<1>().required(),
    settings: yup
      .object({
        font_family: yup.mixed<FontFamilyEnum>().required(),
        label_comparison_value_prefix: yup.string().max(30).defined(),
        label_comparison_value_suffix: yup.string().max(30).defined(),
        label_comparison_value_type: yup
          .mixed<'absolute' | 'percent'>()
          .required(),
        label_font_size: yup.number().integer().min(6).max(32).required(),
        label_placement: yup
          .mixed<'before' | 'after' | 'inside' | 'outside'>()
          .required(),
        label_value_number_of_decimals: yup
          .mixed<'all' | '1' | '2' | '3'>()
          .required(),
        label_value_prefix: yup.string().max(30).defined(),
        label_value_suffix: yup.string().max(30).defined(),
        label_value_type: yup
          .mixed<'amount' | 'percent-neighbor' | 'percent-highest'>()
          .required(),
        node_width: yup.number().integer().min(0).max(200).required(),
        node_height: yup.number().integer().min(1).max(100).required(),
        node_spacing: yup.number().integer().min(1).max(100).required(),
        show_comparison_value: yup.boolean().required(),
        show_imbalances: yup.boolean().required(),
        show_labels: yup.boolean().required(),
        statement_type: yup
          .mixed<'income' | 'cashflow' | 'balance' | 'budget' | 'other'>()
          .required(),
      })
      .required(),
    title: yup
      .object({
        color: yup.string().max(7).required(),
        font_size: yup.number().integer().min(2).max(100).required(),
        value: yup.string().max(255).required(),
        value_override: yup.string().max(255).defined(),
        visible: yup.boolean().required(),
      })
      .required(),
    data: yup
      .object({
        nodes: yup
          .array()
          .of(
            yup.object({
              id: yup.string().required(),
              name: yup.string().required(),
              color: yup.string().max(7).required(),
              offset: yup
                .object({
                  x: yup.number().required(),
                  y: yup.number().required(),
                })
                .required(),
              label_value_override: yup.string().max(255).defined(),
              label_comparison_value_override: yup.string().max(255).defined(),
            })
          )
          .required(),
        flows: yup
          .array()
          .of(
            yup.object({
              id: yup.string().required(),
              color: yup.string().max(7).required(),
              value: yup.number(),
              value_comparison: yup.number(),
              source_node_id: yup.string(),
              target_node_id: yup.string(),
              data_grid_row_index: yup.number().integer().required(),
            })
          )
          .required(),
      })
      .required(),
  }),
  chart_type_list_position: yup.number().integer().min(0),
  chart_type_name: yup.string().max(100),
  company_name: yup
    .string()
    .max(255)
    .required(emptyCompanyNameError)
    .matches(containsNonWhitespace, emptyCompanyNameError),
  comparison_line_prefix: yup.string().max(30),
  comparison_line_suffix: yup.string().max(30).nullable(),
  comparison_line_value_type: yup.mixed<ComparisonLineValueTypeEnum>(),
  created_at: yup.string().nullable(),
  created_with: yup.mixed<CreatedWithEnum>().required(),
  custom_title: customTitleSchema,
  data_origin: yup.mixed<DataOriginEnum>().required(),
  data_source_notes: yup.string().max(200).nullable(),
  download_image_format: yup.mixed<DownloadImageFormatEnum>().nullable(),
  download_scale_png: yup.mixed<DownloadScalePngEnum>().nullable(),
  fiscal_year: yup.number().integer().min(2000).max(2100).required(),
  flows: yup.object().required(),
  font_family: yup.mixed<FontFamilyEnum>().required(),
  font_size: yup.number().integer().min(6).max(32),
  id: yup.number().required(),
  is_example: yup.boolean(),
  is_listed_chart_type_in_footer: yup.boolean(),
  is_main_example: yup.boolean(),
  is_official: yup.boolean().nullable(),
  is_template: yup.boolean(),
  label_pos: yup.mixed<LabelPosEnum>(),
  node_height: yup.number().integer().min(1).max(100),
  node_spacing: yup.number().integer().min(1).max(100),
  node_width: yup.number().integer().min(0).max(200).nullable(),
  nodes: yup.object().required(),
  nr_of_decimals: yup.mixed<NrOfDecimalsEnum>().nullable(),
  owner_id: yup.number().required(),
  points: yup.number().integer().nullable(),
  public_permission: yup.mixed<PublicPermissionEnum>(),
  remembered_node_moves: yup.string().nullable(),
  reporting_period: yup.mixed<ReportingPeriodEnum>().required(),
  second_label_line_value_type: yup.mixed<SecondLabelLineValueTypeEnum>(),
  show_canvas_resolution: yup.boolean(),
  show_comparison_line: yup.boolean().nullable(),
  show_data_editor: yup.boolean(),
  show_imbalances: yup.boolean().nullable(),
  show_labels: yup.boolean().nullable(),
  show_title: yup.boolean().nullable(),
  show_watermark: yup.boolean().nullable(),
  statement_type: yup.mixed<StatementTypeEnum>().nullable(),
  svg_representation: yup.string().nullable(),
  team_permission: yup.mixed<TeamPermissionEnum>(),
  title_font_color: yup.string().max(7).nullable(),
  title_font_size: yup.number().integer().min(2).max(100).nullable(),
  unit_prefix: yup.string().max(30).nullable(),
  unit_suffix: yup.string().max(30).nullable(),
  use_custom_title: yup.boolean().nullable(),
};

export const sankeyObjectSchema: yup.ObjectSchema<SankeyFormValues> = yup
  .object(schema)
  .required();

type SchemaTestParam = { min?: number; max?: number };

const getSchemaTestParams = (key: keyof SankeyFormValues): SchemaTestParam[] =>
  schema[key].tests.map((t) => t.OPTIONS?.params ?? {});

// Form paths form a bigger set than the validation schema keys so we need a
// way to not trigger validation for form paths not covered by the above schema.
const isValidable = (key: SankeyFormPath): key is keyof SankeyFormValues =>
  key in schema;

export const getFieldMinConstraint = (key: SankeyFormPath) =>
  isValidable(key)
    ? getSchemaTestParams(key).find((t) => isNumeric(t.min))?.min
    : undefined;

export const getFieldMaxConstraint = (key: SankeyFormPath) =>
  isValidable(key)
    ? getSchemaTestParams(key).find((t) => isNumeric(t.max))?.max
    : undefined;

// Validates emails
export const emailRegexp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Validates signup passwords
export const nonNumericPasswordRegexp = /^(?!\d+$).{8,}$/;
